import { logReportDownload } from '../../util/auditLogger';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Context from "../../Context";
import { Loading } from "../../components/modules";
import {
  FiRefreshCw,
  FiTrendingUp,
  FiAward,
  FiShoppingBag,
  FiGift,
  FiCheckCircle,
  FiShield,
  FiStar,
  FiGlobe,
  FiChevronDown,
  FiChevronUp,
  FiHeart,
  FiTarget,
  FiActivity,
  FiLayers,
  FiPieChart,
} from "react-icons/fi";
import { RiFileExcel2Line, RiLineFill } from "react-icons/ri";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import * as xlsx from "xlsx";
import dayjs from "dayjs";
import { roundDateToDate } from "../../util";

// Color tokens aligned with Vandee Theme
const COLORS = {
  navy: "#003A9E",
  blue: "#2563eb",
  sky: "#0284c7",
  emerald: "#059669",
  amber: "#d97706",
  rose: "#e11d48",
  purple: "#7c3aed",
  slate: "#475569",
};

const PIE_COLORS = [
  "#003A9E",
  "#059669",
  "#0284c7",
  "#d97706",
  "#7c3aed",
  "#e11d48",
];

const PANELS = [
  { id: "panel-overview", label: "1. ภาพรวมและคำสั่งซื้อ", icon: FiActivity },
  { id: "panel-revenue", label: "2. ยอดขายและการเติบโต", icon: FiTrendingUp },
  { id: "panel-channels", label: "3. ช่องทางและตลาดลาว", icon: FiGlobe },
  { id: "panel-luckydraw", label: "4. สถิติหวยและเพื่อสังคม", icon: FiAward },
  { id: "panel-mastertable", label: "5. ตารางข้อมูลดิบ", icon: FiLayers },
];

export const AnnualReport = () => {
  const { callApi, profileState } = Context();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(
    String(new Date().getFullYear()),
  );
  const [activeChartTab, setActiveChartTab] = useState("both"); // "income" | "tickets" | "both"
  const [exporting, setExporting] = useState(false);
  const [expandedRound, setExpandedRound] = useState(null);
  const [activePanel, setActivePanel] = useState("panel-overview");
  const [loyaltyRound, setLoyaltyRound] = useState("");
  const [discoveredLoyaltyRounds, setDiscoveredLoyaltyRounds] = useState([]);
  const [loyaltyProgress, setLoyaltyProgress] = useState("");
  const [loyaltyMessage, setLoyaltyMessage] = useState("");

  // Fetch annual data from backend
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callApi("dashboard-getYearlySummary", {
        year: selectedYear,
      });
      if (res && res.status && res.data) {
        setData(res.data);
      } else if (res?.toastMessage) {
        window.alert(res.toastMessage, "large");
      }
    } catch (err) {
      console.error("fetchData error:", err);
      window.alert("เกิดข้อผิดพลาดในการโหลดข้อมูลสรุปประจำปี", "large");
    } finally {
      setLoading(false);
    }
  }, [callApi, selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scroll spy to automatically update active panel tab on scroll
  useEffect(() => {
    const handleScroll = () => {
      const threshold = 180;
      let currentPanelId = PANELS[0].id;

      for (const panel of PANELS) {
        const el = document.getElementById(panel.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= threshold) {
            currentPanelId = panel.id;
          }
        }
      }
      setActivePanel(currentPanelId);
    };

    // Attach listeners to window, document and main scrollable containers
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });

    const scrollContainers = document.querySelectorAll(".overflow-y-auto");
    scrollContainers.forEach((container) => {
      container.addEventListener("scroll", handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      scrollContainers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
    };
  }, [data]);

  const summary = data?.summary || {};
  const orderFunnel = data?.orderFunnel || {};
  const lotteryDrawStats = data?.lotteryDrawStats || {};
  const prizeStats = data?.prizeStats || {};
  const communityStats = data?.communityStats || {};
  const rounds = useMemo(() => data?.rounds || [], [data?.rounds]);
  const dayOfWeekStats = useMemo(
    () => data?.dayOfWeekStats || [],
    [data?.dayOfWeekStats],
  );
  const monthlyStats = useMemo(
    () =>
      (data?.monthlyStats || []).filter((m) => m.income > 0 || m.orders > 0),
    [data?.monthlyStats],
  );
  const nationalities = useMemo(
    () => data?.nationalities || [],
    [data?.nationalities],
  );
  const topLoyalCustomers = useMemo(
    () => data?.topLoyalCustomers || [],
    [data?.topLoyalCustomers],
  );

  const loyaltyRoundDates = useMemo(
    () =>
      [
        ...new Set([
          ...rounds.map((r) => r.roundDate),
          ...Object.keys(data?.customerLoyalty?.rounds || {}),
          ...discoveredLoyaltyRounds,
        ]),
      ].sort(),
    [rounds, data?.customerLoyalty, discoveredLoyaltyRounds],
  );
  const missingLoyaltyRounds = loyaltyRoundDates.filter(
    (rd) => !data?.customerLoyalty?.rounds?.[rd],
  );
  const recalculateLoyalty = async (wholeYear) => {
    let targets = wholeYear
      ? loyaltyRoundDates
      : [loyaltyRound || loyaltyRoundDates[loyaltyRoundDates.length - 1]];
    if (!wholeYear && !targets[0]) return;
    setLoyaltyMessage("");
    let completed = 0;
    try {
      if (wholeYear) {
        setLoyaltyProgress("กำลังตรวจสอบงวดทั้งหมดจากคำสั่งซื้อของปีที่เลือก");
        const discovery = await callApi(
          "dashboard-recalculateCustomerLoyalty",
          { year: selectedYear, action: "listRounds" },
        );
        if (!discovery?.status)
          throw new Error(discovery?.toastMessage || "ตรวจสอบงวดไม่สำเร็จ");
        targets = [
          ...new Set([...targets, ...discovery.data.roundDates]),
        ].sort();
        setDiscoveredLoyaltyRounds(targets);
      }
      for (const rd of targets) {
        setLoyaltyProgress(
          `กำลังคำนวณ ${rd} (${completed + 1}/${targets.length})`,
        );
        const res = await callApi("dashboard-recalculateCustomerLoyalty", {
          year: selectedYear,
          roundDate: rd,
        });
        if (!res?.status)
          throw new Error(
            res?.toastMessage || res?.message || "คำนวณไม่สำเร็จ",
          );
        completed += 1;
      }
      setLoyaltyMessage(`คำนวณสำเร็จ ${completed} งวด`);
    } catch (err) {
      setLoyaltyMessage(
        `สำเร็จแล้ว ${completed} งวด หยุดที่ ${targets[completed] || "ขั้นตอนตรวจสอบงวด"}: ${err.message} สามารถเลือกงวดเพื่อคำนวณต่อได้`,
      );
    } finally {
      await fetchData();
      setLoyaltyProgress("");
    }
  };

  // Channel breakdown calculation
  const channelData = useMemo(() => {
    if (!summary.totalApprovedOrders) return [];
    const lineOrders = summary.totalLineOrders || 0;
    const guestOrders = summary.totalGuestOrders || 0;
    const webOrders = Math.max(
      0,
      (summary.totalApprovedOrders || 0) - lineOrders - guestOrders,
    );

    return [
      { name: "เว็บไซต์วันดี สมาชิก", value: webOrders, color: COLORS.navy },
      { name: "LINE ช้อปปิ้ง", value: lineOrders, color: COLORS.emerald },
      { name: "สั่งซื้อแบบทั่วไป", value: guestOrders, color: COLORS.amber },
    ];
  }, [summary]);

  // Scroll to specific panel
  const scrollToPanel = (panelId) => {
    setActivePanel(panelId);
    const element = document.getElementById(panelId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const PRIZE_NAMES = {
    first: "รางวัลที่ 1",
    second: "รางวัลที่ 2",
    third: "รางวัลที่ 3",
    forth: "รางวัลที่ 4",
    fith: "รางวัลที่ 5",
    lastTwo: "เลขท้าย 2 ตัว",
    lastThree: "เลขท้าย 3 ตัว",
    firstThree: "เลขหน้า 3 ตัว",
    nearBy: "รางวัลข้างเคียงรางวัลที่ 1",
  };

  // Comprehensive Excel export
  const handleExportExcel = () => {
    if (!data) return;
    setExporting(true);

    try {
      const wb = xlsx.utils.book_new();
      const exportTime = dayjs().format("YYYY-MM-DD HH:mm:ss");

      // 1. Sheet: ภาพรวมประจำปีและคำสั่งซื้อ
      const overviewRows = [
        ["รายงานสรุปผลการดำเนินงานประจำปี", ""],
        ["ระบบ:", "วันดี"],
        ["ปีสรุปข้อมูล:", `ปี ${selectedYear}`],
        ["เวลาที่ออกเอกสาร:", exportTime],
        [
          "ผู้จัดทำ:",
          `${profileState?.nickname || profileState?.firstName || "Dev/Admin"} (${profileState?.role || "Staff"})`,
        ],
        ["", ""],
        ["ตัวชี้วัดสำคัญ", "ค่าตัวเลข"],
        [
          "ยอดขายรวมทั้งปี",
          `${Number(summary.totalIncome || 0).toLocaleString()} ฿`,
        ],
        [
          "จำนวนสลากที่จำหน่ายได้ทั้งหมด",
          `${Number(summary.totalTicketsSold || 0).toLocaleString()} ใบ`,
        ],
        [
          "โควตาสลากทั้งหมดในระบบ",
          `${Number(summary.totalLottoPool || 0).toLocaleString()} ใบ`,
        ],
        [
          "สลากคงเหลือขายไม่หมดรวมทั้งปี",
          `${Number(summary.totalRemainingTickets || Math.max(0, (summary.totalLottoPool || 0) - (summary.totalTicketsSold || 0))).toLocaleString()} ใบ`,
        ],
        ["อัตราการจำหน่ายสลากหมดเฉลี่ย", `${summary.sellThroughRate || 0} %`],
        [
          "จำนวนคำสั่งซื้อที่สำเร็จทั้งหมด",
          `${Number(summary.totalApprovedOrders || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "จำนวนสลากเฉลี่ยต่อคำสั่งซื้อ",
          `${summary.averageTicketPerOrder || 0} ใบ`,
        ],
        [
          "ยอดใช้จ่ายเฉลี่ยต่อคำสั่งซื้อ",
          `${Number(summary.averageSpendingPerOrder || 0).toLocaleString()} ฿`,
        ],
        ["", ""],
        ["สถิติผลรางวัลทั้งปี", "ค่าตัวเลข"],
        [
          "จำนวนผู้ถูกรางวัลรวม",
          `${Number(summary.totalWinnersCount || summary.totalWinningPrizes || 0).toLocaleString()} คน`,
        ],
        [
          "สลากที่ลูกค้าถูกรางวัลรวม",
          `${Number(summary.totalWinningTickets || 0).toLocaleString()} ใบ`,
        ],
        [
          "มูลค่าเงินรางวัลที่มอบให้ลูกค้ารวม",
          `${Number(summary.totalCustomerPrizeAmount || 0).toLocaleString()} ฿`,
        ],
        [
          "สลากที่หลุดรางวัลในสต็อกรวม",
          `${Number(summary.totalUnsoldWinningTickets || 0).toLocaleString()} ใบ`,
        ],
        [
          "มูลค่ารางวัลในสลากค้างสต็อกรวม",
          `${Number(summary.totalUnsoldPrizeAmount || 0).toLocaleString()} ฿`,
        ],
        ["", ""],
        ["ช่องทางและการตลาดเพิ่มเติม", "ค่าตัวเลข"],
        [
          "ยอดขายผ่าน LINE ช้อปปิ้ง",
          `${Number(summary.totalLineIncome || 0).toLocaleString()} ฿`,
        ],
        [
          "จำนวนออเดอร์ผ่าน LINE ช้อปปิ้ง",
          `${Number(summary.totalLineOrders || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "ยอดขายตลาดประเทศลาว",
          `${Number(summary.totalLaoIncomeLak || 0).toLocaleString()} กีบ`,
        ],
        [
          "จำนวนออเดอร์ตลาดประเทศลาว",
          `${Number(summary.totalLaoOrders || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "จำนวนออเดอร์ประเภททั่วไป",
          `${Number(summary.totalGuestOrders || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "สมาชิกใหม่ที่สมัครเข้าร่วมระบบในปีนี้",
          `${Number(summary.newUsersCount || 0).toLocaleString()} คน`,
        ],
        ["จำนวนงวดที่เปิดจำหน่ายทั้งหมด", `${summary.roundsCount || 0} งวด`],
        ["", ""],
        ["สถานะคำสั่งซื้อทั้งปี", "ค่าตัวเลข"],
        [
          "คำสั่งซื้อทั้งหมดที่เกิดขึ้นในระบบ",
          `${Number(orderFunnel.total || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "คำสั่งซื้อที่สำเร็จ",
          `${Number(orderFunnel.approved || 0).toLocaleString()} ออเดอร์`,
        ],
        [
          "คำสั่งซื้อที่ยกเลิก",
          `${Number(orderFunnel.canceled || 0).toLocaleString()} ออเดอร์`,
        ],
        ["อัตราความสำเร็จในการสั่งซื้อ", `${orderFunnel.approvalRate || 0} %`],
        ["", ""],
        ["กิจกรรมเพื่อสังคมและภารกิจ", "ค่าตัวเลข"],
        [
          "จำนวนคำสั่งซื้อร่วมบริจาคการกุศล",
          `${Number(communityStats.donateOrdersCount || 0).toLocaleString()} รายการ`,
        ],
        [
          "จำนวนภารกิจที่สมาชิกทำสำเร็จ",
          `${Number(communityStats.missionOrdersCount || 0).toLocaleString()} รายการ`,
        ],
      ];
      const wsOverview = xlsx.utils.aoa_to_sheet(overviewRows);
      wsOverview["!cols"] = [{ wch: 42 }, { wch: 30 }];
      xlsx.utils.book_append_sheet(wb, wsOverview, "ภาพรวมและสถานะ");

      // 2. Sheet: สรุปผลงานรายงวด
      const roundHeaders = [
        "งวดวันที่",
        "วันที่ออกรางวัล",
        "รางวัลที่ 1",
        "เลขท้าย 2 ตัว",
        "ยอดขาย",
        "สลากจำหน่ายได้",
        "สลากเหลือขายไม่หมด",
        "โควตาสลาก",
        "อัตราขายหมด",
        "ผู้ถูกรางวัล",
        "สลากถูกรางวัล",
        "เงินรางวัลลูกค้า",
        "สลากหลุดรางวัล",
        "มูลค่ารางวัลที่ไม่ได้ขาย",
        "ออเดอร์สำเร็จ",
        "ออเดอร์ยกเลิก",
        "ยอดใช้จ่ายเฉลี่ย",
        "สลากเฉลี่ย",
        "ยอดขาย LINE ช้อปปิ้ง",
        "ออเดอร์ LINE",
        "ยอดขายลาว",
        "ออเดอร์ลาว",
        "ออเดอร์ทั่วไป",
        "หมายเหตุเด่น",
      ];
      const roundRows = rounds.map((r) => [
        r.roundDate,
        roundDateToDate(r.roundDate) || r.roundDate,
        r.firstPrize || "-",
        r.lastTwo || "-",
        `${Number(r.income).toLocaleString()} ฿`,
        `${Number(r.ticketsSold).toLocaleString()} ใบ`,
        `${Number(r.remainingTickets ?? Math.max(0, r.allocated - r.ticketsSold)).toLocaleString()} ใบ`,
        `${Number(r.allocated).toLocaleString()} ใบ`,
        `${r.sellThroughRate} %`,
        `${Number(r.winnerCount || 0).toLocaleString()} คน`,
        `${Number(r.soldWinningTickets || 0).toLocaleString()} ใบ`,
        `${Number(r.soldPrizeAmount || 0).toLocaleString()} ฿`,
        r.unsoldWinningTickets > 0
          ? `${Number(r.unsoldWinningTickets).toLocaleString()} ใบ`
          : "-",
        r.unsoldPrizeAmount > 0
          ? `${Number(r.unsoldPrizeAmount).toLocaleString()} ฿`
          : "-",
        r.orders,
        r.canceledOrders || 0,
        `${Number(r.avgSpending || 0).toLocaleString()} ฿`,
        `${r.avgTickets || 0} ใบ`,
        r.lineIncome > 0 ? `${Number(r.lineIncome).toLocaleString()} ฿` : "-",
        r.lineOrders || 0,
        r.laoIncomeLak > 0
          ? `${Number(r.laoIncomeLak).toLocaleString()} กีบ`
          : "-",
        r.laoOrders || 0,
        r.guestOrders || 0,
        r.isBestIncome
          ? "ยอดขายสูงสุดแห่งปี"
          : r.isBestTickets
            ? "จำหน่ายสลากได้มากที่สุด"
            : "-",
      ]);
      const wsRounds = xlsx.utils.aoa_to_sheet([roundHeaders, ...roundRows]);
      wsRounds["!cols"] = [
        { wch: 15 },
        { wch: 20 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 14 },
        { wch: 14 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 18 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
        { wch: 15 },
        { wch: 20 },
        { wch: 14 },
        { wch: 18 },
        { wch: 12 },
        { wch: 14 },
        { wch: 24 },
      ];
      xlsx.utils.book_append_sheet(wb, wsRounds, "สรุปผลงานรายงวด");

      // 3. Sheet: สถิติผลสลากและเลขเด่น
      const drawHeaders = [
        "เลขท้าย 2 ตัวที่ออกบ่อยสุด",
        "จำนวนครั้งที่ออกรางวัล",
      ];
      const drawRows = (lotteryDrawStats.topDrawnNumbers || []).map((d) => [
        d.number,
        `${d.count} ครั้ง`,
      ]);
      const wsDraws = xlsx.utils.aoa_to_sheet([drawHeaders, ...drawRows]);
      wsDraws["!cols"] = [{ wch: 25 }, { wch: 22 }];
      xlsx.utils.book_append_sheet(wb, wsDraws, "สถิติผลสลาก");

      // 4. Sheet: พฤติกรรมตามวันและสัญชาติ
      const behaviorRows = [
        ["ยอดขายและคำสั่งซื้อตามวันในสัปดาห์", "", "", ""],
        ["วันในสัปดาห์", "ยอดขายรวม", "จำนวนออเดอร์", "จำนวนสลาก"],
        ...dayOfWeekStats.map((d) => [
          d.dayName,
          `${Number(d.income).toLocaleString()} ฿`,
          d.orders,
          `${Number(d.tickets).toLocaleString()} ใบ`,
        ]),
        ["", "", "", ""],
        ["สัดส่วนคำสั่งซื้อตามสัญชาติผู้ใช้งาน", "", "", ""],
        ["สัญชาติ", "รหัส", "จำนวนคำสั่งซื้อ", "สัดส่วน"],
        ...nationalities.map((n) => {
          const totalOrders = summary.totalApprovedOrders || 1;
          const pct = ((n.orders / totalOrders) * 100).toFixed(2);
          return [n.label, n.code, n.orders, `${pct} %`];
        }),
      ];
      const wsBehavior = xlsx.utils.aoa_to_sheet(behaviorRows);
      wsBehavior["!cols"] = [
        { wch: 22 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
      ];
      xlsx.utils.book_append_sheet(wb, wsBehavior, "พฤติกรรมลูกค้า");

      // 5. Sheet: ลูกค้าประจำแห่งปี
      const loyalHeaders = [
        "อันดับ",
        "รหัสสมาชิก",
        "ชื่อและนามสกุล",
        "เบอร์โทรศัพท์",
        "จำนวนงวดที่ซื้อสำเร็จ",
        "จำนวนใบรวม",
        "ยอดซื้อรวม (บาท)",
      ];
      const loyalRows = topLoyalCustomers.map((u, idx) => [
        idx + 1,
        u.userId,
        u.name || "-",
        u.phone || "-",
        `${u.roundsAppeared} งวด`,
        u.tickets || 0,
        u.amount || 0,
      ]);
      const wsLoyal = xlsx.utils.aoa_to_sheet([loyalHeaders, ...loyalRows]);
      wsLoyal["!cols"] = [
        { wch: 8 },
        { wch: 18 },
        { wch: 28 },
        { wch: 16 },
        { wch: 32 },
      ];
      xlsx.utils.book_append_sheet(wb, wsLoyal, "ลูกค้าประจำแห่งปี");

      // Trigger download
      logReportDownload();
      xlsx.writeFile(
        wb,
        `รายงานสรุปประจำปี_วันดี_${selectedYear}_จัดทำเมื่อ_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
      );
    } catch (err) {
      console.error("Export error:", err);
      window.alert("ไม่สามารถสร้างไฟล์ Excel ได้ โปรดลองอีกครั้ง", "large");
    } finally {
      setExporting(false);
    }
  };

  const bestIncomeRound = useMemo(
    () => rounds.find((r) => r.isBestIncome) || rounds[0],
    [rounds],
  );
  const bestTicketsRound = useMemo(
    () => rounds.find((r) => r.isBestTickets) || rounds[0],
    [rounds],
  );

  // Framer Motion Animation Variants for slide panels
  const panelVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.45, ease: "easeOut" },
    },
  };

  return (
    <div className="flex flex-col gap-6 p-2 sm:p-4 max-w-[1440px] mx-auto min-h-screen pb-24">
      {/* Sticky Executive Control & Quick Jump Bar */}
      <div className="sticky top-2 z-30 bg-white/95 dark:bg-[#22272e]/95 backdrop-blur-md p-3.5 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        {/* Title & Badge */}
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            <FiAward className="text-xl" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                วันดี สรุปประจำปี {selectedYear}
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
                <FiShield className="text-xs" /> เฉพาะ Dev และ SuperAdmin
              </span>
            </div>
            <p className="text-[11px] text-slate-500 font-medium">
              สถิติภาพรวม 5 มิติ นำเสนอข้อมูลประจำปี
            </p>
          </div>
        </div>

        {/* Quick Jump Buttons with Real-Time Active State */}
        <div className="hidden xl:flex items-center gap-1 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-md text-xs font-semibold">
          {PANELS.map((p) => {
            const IconComponent = p.icon;
            const isCurrent = activePanel === p.id;
            return (
              <button
                key={p.id}
                onClick={() => scrollToPanel(p.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 ${
                  isCurrent
                    ? "bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm font-bold scale-[1.02]"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <IconComponent
                  className={`text-xs ${isCurrent ? "text-blue-600 dark:text-blue-400" : ""}`}
                />
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Actions: Refresh & Single Unified Excel Button */}
        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          <select
            aria-label="ปีรายงาน"
            value={selectedYear}
            disabled={loading || !!loyaltyProgress}
            className="border rounded p-2 text-xs dark:bg-slate-800"
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setLoyaltyRound("");
              setDiscoveredLoyaltyRounds([]);
              setLoyaltyMessage("");
            }}
          >
            {Array.from(
              { length: Math.max(1, new Date().getFullYear() - 2019) },
              (_, i) => String(new Date().getFullYear() - i),
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            onClick={fetchData}
            disabled={loading || !!loyaltyProgress}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition-colors"
            title="รีเฟรชข้อมูลล่าสุด"
          >
            <FiRefreshCw
              className={`text-xs ${loading ? "animate-spin" : ""}`}
            />
            รีเฟรช
          </button>

          <button
            onClick={handleExportExcel}
            disabled={loading || exporting || !data}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-95 rounded-md shadow-sm transition-all disabled:opacity-50"
            title="ดาวน์โหลดข้อมูลทั้งหมดเป็นไฟล์ Excel จัดระเบียบแยก 5 แท็บ"
          >
            <RiFileExcel2Line className="text-base" />
            {exporting ? "กำลังประมวลผล..." : "ดาวน์โหลดสรุปทั้งปี"}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 bg-white dark:bg-[#22272e] rounded-md border border-slate-200 dark:border-slate-800">
          <Loading />
          <p className="text-sm text-slate-500 font-semibold">
            กำลังคำนวณข้อมูลประจำปี...
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {/* =========================================================================
              PANEL 1: EXECUTIVE OVERVIEW & ORDER FUNNEL
              ========================================================================= */}
          <motion.section
            id="panel-overview"
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="bg-white dark:bg-[#22272e] p-6 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 scroll-mt-24"
          >
            {/* Panel Header */}
            <div className="flex justify-between items-center pb-4 mb-5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 flex items-center justify-center font-bold text-sm">
                  1
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    ภาพรวมผลการดำเนินงานและสัดส่วนคำสั่งซื้อ
                  </h2>
                  <p className="text-xs text-slate-500">
                    ตัวชี้วัดสำคัญและอัตราความสำเร็จของคำสั่งซื้อตลอดทั้งปี{" "}
                    {selectedYear}
                  </p>
                </div>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded">
                รวม {summary.roundsCount || 0} งวด
              </span>
            </div>

            {/* 4 Primary KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Total Income */}
              <div className="p-4 rounded-md bg-slate-50/70 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 relative overflow-hidden">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  ยอดขายรวมทั้งปี {selectedYear}
                </p>
                <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white font-mono">
                  <CountUp
                    end={summary.totalIncome || 0}
                    separator=","
                    duration={1.5}
                  />{" "}
                  ฿
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  เฉลี่ย{" "}
                  <strong className="text-slate-700 dark:text-slate-300">
                    {Number(
                      Math.round(
                        (summary.totalIncome || 0) / (summary.roundsCount || 1),
                      ),
                    ).toLocaleString()}{" "}
                    ฿
                  </strong>{" "}
                  / งวด
                </div>
              </div>

              {/* Total Tickets Sold */}
              <div className="p-4 rounded-md bg-slate-50/70 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 relative overflow-hidden">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  สลากจำหน่ายได้
                </p>
                <div className="mt-2 text-2xl font-bold text-emerald-600 font-mono">
                  <CountUp
                    end={summary.totalTicketsSold || 0}
                    separator=","
                    duration={1.5}
                  />{" "}
                  ใบ
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    โควตา {Number(summary.totalLottoPool || 0).toLocaleString()}{" "}
                    ใบ
                  </span>
                  <span className="font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded">
                    ขายหมด {summary.sellThroughRate || 0} %
                  </span>
                </div>
              </div>

              {/* Total Orders Approved */}
              <div className="p-4 rounded-md bg-slate-50/70 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 relative overflow-hidden">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  คำสั่งซื้อที่สำเร็จ
                </p>
                <div className="mt-2 text-2xl font-bold text-blue-600 font-mono">
                  <CountUp
                    end={summary.totalApprovedOrders || 0}
                    separator=","
                    duration={1.5}
                  />{" "}
                  ออเดอร์
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  เฉลี่ย{" "}
                  <strong className="text-slate-700 dark:text-slate-300">
                    {summary.averageTicketPerOrder || 0} ใบ
                  </strong>{" "}
                  / ออเดอร์
                </div>
              </div>

              {/* New Users & Winners */}
              <div className="p-4 rounded-md bg-slate-50/70 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/60 relative overflow-hidden">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  สมาชิกใหม่และรางวัลปีนี้
                </p>
                <div className="mt-2 text-2xl font-bold text-purple-600 font-mono">
                  +
                  <CountUp
                    end={summary.newUsersCount || 0}
                    separator=","
                    duration={1.5}
                  />{" "}
                  คน
                </div>
                <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                  <FiAward className="text-amber-500" />
                  <span>
                    ถูกรางวัลรวม{" "}
                    <strong className="text-slate-800 dark:text-white font-mono">
                      {Number(summary.totalWinningPrizes || 0).toLocaleString()}
                    </strong>{" "}
                    ครั้ง
                  </span>
                </div>
              </div>
            </div>

            {/* Order Fulfillment Funnel Bar */}
            <div className="p-4 rounded-md bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2.5 gap-2">
                <div className="flex items-center gap-2">
                  <FiActivity className="text-blue-600 text-sm" />
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                    อัตราความสำเร็จของคำสั่งซื้อ:{" "}
                    {orderFunnel.approvalRate || 0} %
                  </span>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-3">
                  <span>
                    ทั้งหมด:{" "}
                    <strong className="font-mono text-slate-800 dark:text-slate-200">
                      {Number(orderFunnel.total || 0).toLocaleString()}
                    </strong>
                  </span>
                  <span className="text-emerald-600 font-bold">
                    สำเร็จ: {Number(orderFunnel.approved || 0).toLocaleString()}
                  </span>
                  <span className="text-rose-600 font-semibold">
                    ยกเลิก: {Number(orderFunnel.canceled || 0).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="w-full bg-slate-200 dark:bg-slate-700 h-3 rounded-full flex overflow-hidden">
                <div
                  className="bg-emerald-500 h-full transition-all duration-1000"
                  style={{
                    width: `${Math.min(100, orderFunnel.approvalRate || 0)}%`,
                  }}
                  title={`สำเร็จ: ${orderFunnel.approvalRate}%`}
                />
                <div
                  className="bg-rose-400 h-full transition-all duration-1000"
                  style={{
                    width: `${Math.max(0, 100 - (orderFunnel.approvalRate || 0))}%`,
                  }}
                  title="ยกเลิกหรือหมดเวลา"
                />
              </div>
            </div>
          </motion.section>

          {/* =========================================================================
              PANEL 2: REVENUE & SALES TRAJECTORY
              ========================================================================= */}
          <motion.section
            id="panel-revenue"
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="bg-white dark:bg-[#22272e] p-6 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 scroll-mt-24"
          >
            {/* Panel Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 mb-5 border-b border-slate-100 dark:border-slate-800 gap-3">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex items-center justify-center font-bold text-sm">
                  2
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    ยอดขายและแนวโน้มการเติบโต
                  </h2>
                  <p className="text-xs text-slate-500">
                    วิเคราะห์เส้นทางยอดขาย จำนวนสลาก
                    และพฤติกรรมยอดซื้อเฉลี่ยต่องวด
                  </p>
                </div>
              </div>

              {/* Chart Mode Controls */}
              <div className="inline-flex rounded-md p-1 bg-slate-100 dark:bg-slate-800 text-xs font-semibold">
                <button
                  onClick={() => setActiveChartTab("income")}
                  className={`px-3 py-1 rounded transition-colors ${
                    activeChartTab === "income"
                      ? "bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-300 shadow-sm"
                      : "text-slate-600 dark:text-slate-400"
                  }`}
                >
                  ยอดขาย
                </button>
                <button
                  onClick={() => setActiveChartTab("tickets")}
                  className={`px-3 py-1 rounded transition-colors ${
                    activeChartTab === "tickets"
                      ? "bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-sm"
                      : "text-slate-600 dark:text-slate-400"
                  }`}
                >
                  จำนวนสลาก
                </button>
                <button
                  onClick={() => setActiveChartTab("both")}
                  className={`px-3 py-1 rounded transition-colors ${
                    activeChartTab === "both"
                      ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                      : "text-slate-600 dark:text-slate-400"
                  }`}
                >
                  แสดงคู่กัน
                </button>
              </div>
            </div>

            {/* Top Highlights Banner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-md bg-gradient-to-r from-blue-900 to-blue-950 text-white flex justify-between items-center">
                <div>
                  <span className="text-[11px] font-bold text-blue-200 uppercase tracking-wider">
                    งวดที่ทำยอดขายสูงสุดแห่งปี
                  </span>
                  <div className="text-xl font-bold font-mono mt-1">
                    {bestIncomeRound
                      ? Number(bestIncomeRound.income || 0).toLocaleString()
                      : 0}{" "}
                    ฿
                  </div>
                  <p className="text-xs text-blue-300 mt-0.5">
                    งวดวันที่:{" "}
                    {roundDateToDate(bestIncomeRound?.roundDate) ||
                      bestIncomeRound?.roundDate}
                  </p>
                </div>
                <div className="text-right text-xs text-blue-200">
                  <p>
                    {Number(bestIncomeRound?.ticketsSold || 0).toLocaleString()}{" "}
                    ใบ
                  </p>
                  <p>
                    {Number(bestIncomeRound?.orders || 0).toLocaleString()}{" "}
                    ออเดอร์
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-md bg-gradient-to-r from-emerald-900 to-emerald-950 text-white flex justify-between items-center">
                <div>
                  <span className="text-[11px] font-bold text-emerald-200 uppercase tracking-wider">
                    งวดที่จำหน่ายสลากได้มากที่สุด
                  </span>
                  <div className="text-xl font-bold font-mono mt-1">
                    {bestTicketsRound
                      ? Number(
                          bestTicketsRound.ticketsSold || 0,
                        ).toLocaleString()
                      : 0}{" "}
                    ใบ
                  </div>
                  <p className="text-xs text-emerald-300 mt-0.5">
                    งวดวันที่:{" "}
                    {roundDateToDate(bestTicketsRound?.roundDate) ||
                      bestTicketsRound?.roundDate}
                  </p>
                </div>
                <div className="text-right text-xs text-emerald-200">
                  <p>
                    {Number(bestTicketsRound?.income || 0).toLocaleString()} ฿
                  </p>
                  <p>ขายหมด {bestTicketsRound?.sellThroughRate || 0} %</p>
                </div>
              </div>
            </div>

            {/* Main Composed Chart */}
            <div className="h-[340px] w-full mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={rounds}
                  margin={{ top: 10, right: 15, left: 15, bottom: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#e2e8f0"
                  />
                  <XAxis
                    dataKey="roundDate"
                    tick={{ fontSize: 11 }}
                    angle={-35}
                    textAnchor="end"
                    tickFormatter={(val) => {
                      const d = dayjs(val);
                      return d.isValid() ? d.format("D/M") : val;
                    }}
                  />
                  {(activeChartTab === "income" ||
                    activeChartTab === "both") && (
                    <YAxis
                      yAxisId="left"
                      orientation="left"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M ฿`}
                    />
                  )}
                  {(activeChartTab === "tickets" ||
                    activeChartTab === "both") && (
                    <YAxis
                      yAxisId={activeChartTab === "both" ? "right" : "left"}
                      orientation={activeChartTab === "both" ? "right" : "left"}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `${(v / 1000).toFixed(0)}k ใบ`}
                    />
                  )}
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const item = payload[0]?.payload;
                      return (
                        <div className="bg-white dark:bg-[#1c2128] p-3 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 text-xs">
                          <p className="font-bold text-slate-800 dark:text-slate-200 border-b pb-1 mb-2">
                            งวดวันที่: {roundDateToDate(label) || label}
                          </p>
                          <div className="flex flex-col gap-1">
                            <p className="flex justify-between gap-4 text-blue-600 font-semibold">
                              <span>ยอดขาย:</span>
                              <span className="font-mono">
                                {Number(item.income || 0).toLocaleString()} ฿
                              </span>
                            </p>
                            <p className="flex justify-between gap-4 text-emerald-600 font-semibold">
                              <span>สลากจำหน่ายได้:</span>
                              <span className="font-mono">
                                {Number(item.ticketsSold || 0).toLocaleString()}{" "}
                                ใบ
                              </span>
                            </p>
                            <p className="flex justify-between gap-4 text-slate-500">
                              <span>เฉลี่ยต่อออเดอร์:</span>
                              <span className="font-mono">
                                {Number(item.avgSpending || 0).toLocaleString()}{" "}
                                ฿
                              </span>
                            </p>
                            <p className="flex justify-between gap-4 text-amber-600 font-semibold">
                              <span>อัตราขายหมด:</span>
                              <span className="font-mono">
                                {item.sellThroughRate} %
                              </span>
                            </p>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: 10, fontSize: 12 }} />

                  {(activeChartTab === "income" ||
                    activeChartTab === "both") && (
                    <Bar
                      yAxisId="left"
                      dataKey="income"
                      name="ยอดขาย"
                      fill={COLORS.navy}
                      radius={[4, 4, 0, 0]}
                    />
                  )}
                  {(activeChartTab === "tickets" ||
                    activeChartTab === "both") && (
                    <Line
                      yAxisId={activeChartTab === "both" ? "right" : "left"}
                      type="monotone"
                      dataKey="ticketsSold"
                      name="สลากจำหน่ายได้"
                      stroke={COLORS.emerald}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: COLORS.emerald }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Monthly Trend Area Chart */}
            <div className="p-4 rounded-md bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800">
              <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1">
                การเติบโตสะสมรายเดือน
              </h3>
              <div className="h-[180px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={monthlyStats}
                    margin={{ top: 5, right: 10, left: 15, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="monthGrad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={COLORS.blue}
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor={COLORS.blue}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis dataKey="monthName" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M ฿`}
                    />
                    <Tooltip
                      formatter={(v) => [
                        `${Number(v).toLocaleString()} ฿`,
                        "ยอดขาย",
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="income"
                      stroke={COLORS.blue}
                      strokeWidth={2}
                      fill="url(#monthGrad)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.section>

          {/* =========================================================================
              PANEL 3: CHANNELS & INTERNATIONAL MARKETS
              ========================================================================= */}
          <motion.section
            id="panel-channels"
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="bg-white dark:bg-[#22272e] p-6 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 scroll-mt-24"
          >
            {/* Panel Header */}
            <div className="flex justify-between items-center pb-4 mb-5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex items-center justify-center font-bold text-sm">
                  3
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    ช่องทางการจำหน่ายและตลาดต่างประเทศ
                  </h2>
                  <p className="text-xs text-slate-500">
                    วิเคราะห์สัดส่วนคำสั่งซื้อจาก LINE ช้อปปิ้ง ตลาดประเทศลาว
                    และสัญชาติลูกค้า
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Sales Channel Donut */}
              <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col justify-between">
                <div>
                  <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1 flex items-center gap-1.5">
                    <FiPieChart className="text-emerald-600" />{" "}
                    สัดส่วนคำสั่งซื้อตามช่องทาง
                  </h3>
                  <p className="text-[11px] text-slate-500 mb-3">
                    เว็บไซต์วันดี LINE ช้อปปิ้ง และสั่งซื้อทั่วไป
                  </p>
                </div>

                <div className="h-[180px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={channelData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={3}
                      >
                        {channelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val) => [
                          `${Number(val).toLocaleString()} ออเดอร์ (${(
                            (val / (summary.totalApprovedOrders || 1)) *
                            100
                          ).toFixed(1)}%)`,
                          "สัดส่วน",
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-200/60 text-xs">
                  {channelData.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        {item.name}
                      </span>
                      <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                        {Number(item.value).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lao Market Spotlight */}
              <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 text-white flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-xs text-blue-300 font-bold mb-2">
                    <span className="flex items-center gap-1.5">
                      <FiGlobe /> ตลาดประเทศลาว
                    </span>
                    <span className="bg-slate-800 px-2 py-0.5 rounded text-[10px]">
                      สกุลเงินกีบ
                    </span>
                  </div>
                  <div className="text-2xl font-bold font-mono text-emerald-300 mt-2">
                    {Number(summary.totalLaoIncomeLak || 0).toLocaleString()}{" "}
                    กีบ
                  </div>
                  <p className="text-xs text-slate-300 mt-1">
                    ยอดคำสั่งซื้อสำเร็จรวม:{" "}
                    <strong className="text-white">
                      {Number(summary.totalLaoOrders || 0).toLocaleString()}
                    </strong>{" "}
                    ออเดอร์
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-800 flex justify-between text-xs text-slate-400">
                  <span>
                    สลากจำหน่าย:{" "}
                    {Number(summary.totalLaoTickets || 0).toLocaleString()} ใบ
                  </span>
                  <span className="text-amber-300">ตลาดเติบโตต่อเนื่อง</span>
                </div>
              </div>

              {/* Nationalities Distribution */}
              <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col justify-between">
                <div>
                  <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-1 flex items-center gap-1.5">
                    <FiGlobe className="text-purple-600" />{" "}
                    สัดส่วนสัญชาติผู้ซื้อ
                  </h3>
                  <p className="text-[11px] text-slate-500 mb-3">
                    การกระจายตัวของฐานลูกค้าตามประเทศ
                  </p>
                </div>

                <div className="flex flex-col gap-2.5">
                  {nationalities.map((item, idx) => {
                    const total = summary.totalApprovedOrders || 1;
                    const pct = Math.min(
                      100,
                      Number(((item.orders / total) * 100).toFixed(2)),
                    );
                    return (
                      <div key={idx} className="flex flex-col gap-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-semibold text-slate-700 dark:text-slate-200">
                            {item.label}
                          </span>
                          <span className="font-mono text-slate-500">
                            {Number(item.orders).toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 h-1.5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max(1, pct)}%`,
                              backgroundColor:
                                PIE_COLORS[idx % PIE_COLORS.length],
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-[11px] text-slate-400 mt-2">
                  * ข้อมูลตรวจนับจากคำสั่งซื้อที่อนุมัติแล้ว
                </p>
              </div>
            </div>
          </motion.section>

          {/* =========================================================================
              PANEL 4: LUCKY DRAW & COMMUNITY (CSR)
              ========================================================================= */}
          <motion.section
            id="panel-luckydraw"
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="bg-white dark:bg-[#22272e] p-6 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 scroll-mt-24"
          >
            {/* Panel Header */}
            <div className="flex justify-between items-center pb-4 mb-5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 flex items-center justify-center font-bold text-sm">
                  4
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    สถิติผลรางวัล สลากคงเหลือ และกิจกรรมเพื่อสังคม
                  </h2>
                  <p className="text-xs text-slate-500">
                    สรุปผลรางวัลที่ลูกค้าได้รับ สลากที่หลุดรางวัลในสต็อก
                    และผลตอบแทนสู่สังคม
                  </p>
                </div>
              </div>
            </div>

            {/* Prize Highlight KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Card 1: Sold Winning Tickets */}
              <div className="p-4 rounded-md border border-emerald-100 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/60 to-white dark:from-slate-800 dark:to-slate-900 shadow-2xs">
                <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <FiAward /> สลากที่ลูกค้าถูกรางวัล
                  </span>
                  <span className="text-[10px] bg-emerald-100 dark:bg-emerald-950 px-1.5 py-0.5 rounded">
                    ส่งมอบแล้ว
                  </span>
                </div>
                <div className="text-2xl font-bold font-mono text-slate-800 dark:text-white mt-2">
                  <CountUp
                    end={
                      summary.totalWinningTickets ||
                      prizeStats.totalSoldWinningTickets ||
                      0
                    }
                    separator=","
                    duration={1.5}
                  />{" "}
                  ใบ
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  จากผู้โชคดีทั้งหมด{" "}
                  <strong className="text-emerald-700 dark:text-emerald-400">
                    {Number(summary.totalWinnersCount || 0).toLocaleString()}
                  </strong>{" "}
                  ท่าน
                </p>
              </div>

              {/* Card 2: Total Customer Prize Amount */}
              <div className="p-4 rounded-md border border-blue-100 dark:border-blue-900/50 bg-gradient-to-br from-blue-50/60 to-white dark:from-slate-800 dark:to-slate-900 shadow-2xs">
                <div className="flex items-center justify-between text-blue-600 dark:text-blue-400 text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <FiGift /> เงินรางวัลลูกค้าได้รับรวม
                  </span>
                  <span className="text-[10px] bg-blue-100 dark:bg-blue-950 px-1.5 py-0.5 rounded">
                    ทั้งปี
                  </span>
                </div>
                <div className="text-2xl font-bold font-mono text-blue-700 dark:text-blue-400 mt-2">
                  <CountUp
                    end={
                      summary.totalCustomerPrizeAmount ||
                      prizeStats.totalCustomerPrizeAmount ||
                      0
                    }
                    separator=","
                    duration={1.5}
                  />{" "}
                  ฿
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  มูลค่ารางวัลที่ระบบวันดีส่งมอบให้สมาชิก
                </p>
              </div>

              {/* Card 3: Unsold Winning Tickets */}
              <div className="p-4 rounded-md border border-amber-200/60 dark:border-amber-900/50 bg-gradient-to-br from-amber-50/60 to-white dark:from-slate-800 dark:to-slate-900 shadow-2xs">
                <div className="flex items-center justify-between text-amber-700 dark:text-amber-400 text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <FiShield /> รางวัลในสลากค้างสต็อก
                  </span>
                  <span className="text-[10px] bg-amber-100 dark:bg-amber-950 px-1.5 py-0.5 rounded">
                    หลุดรางวัล
                  </span>
                </div>
                <div className="text-2xl font-bold font-mono text-amber-700 dark:text-amber-400 mt-2">
                  <CountUp
                    end={
                      summary.totalUnsoldWinningTickets ||
                      prizeStats.totalUnsoldWinningTickets ||
                      0
                    }
                    separator=","
                    duration={1.5}
                  />{" "}
                  ใบ
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  สลากที่ขายไม่ออกแต่ตรงกับผลรางวัล
                </p>
              </div>

              {/* Card 4: Unsold Prize Amount */}
              <div className="p-4 rounded-md border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 shadow-2xs">
                <div className="flex items-center justify-between text-slate-600 dark:text-slate-400 text-xs font-bold">
                  <span className="flex items-center gap-1.5">
                    <FiActivity /> มูลค่ารางวัลที่ค้างสต็อก
                  </span>
                  <span className="text-[10px] bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                    สลากคงเหลือ
                  </span>
                </div>
                <div className="text-2xl font-bold font-mono text-slate-700 dark:text-slate-200 mt-2">
                  <CountUp
                    end={
                      summary.totalUnsoldPrizeAmount ||
                      prizeStats.totalUnsoldPrizeAmount ||
                      0
                    }
                    separator=","
                    duration={1.5}
                  />{" "}
                  ฿
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  สลากคงเหลือสะสมทั้งปี{" "}
                  {Number(
                    summary.totalRemainingTickets ||
                      Math.max(
                        0,
                        (summary.totalLottoPool || 0) -
                          (summary.totalTicketsSold || 0),
                      ),
                  ).toLocaleString()}{" "}
                  ใบ
                </p>
              </div>
            </div>

            {/* Prize Comparison Chart across rounds */}
            <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 mb-6">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200">
                    เปรียบเทียบมูลค่าเงินรางวัลที่ลูกค้าได้รับ vs
                    รางวัลในสลากค้างสต็อกรายงวด
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    แสดงมูลค่าเงินรางวัลที่แตกในแต่ละงวด
                    และรางวัลที่หลุดอยู่ในสลากที่ขายไม่หมด
                  </p>
                </div>
              </div>
              <div className="h-[230px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={rounds}
                    margin={{ top: 10, right: 10, left: 15, bottom: 25 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="roundDate"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(d) =>
                        roundDateToDate(d)
                          ?.split(" ")
                          ?.slice(0, 2)
                          ?.join(" ") || d
                      }
                      angle={-25}
                      textAnchor="end"
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) =>
                        v >= 1000000
                          ? `${(v / 1000000).toFixed(1)}M ฿`
                          : `${(v / 1000).toFixed(0)}k ฿`
                      }
                    />
                    <Tooltip
                      formatter={(v, name) => [
                        `${Number(v).toLocaleString()} ฿`,
                        name === "soldPrizeAmount"
                          ? "เงินรางวัลลูกค้า"
                          : "รางวัลในสลากค้างสต็อก",
                      ]}
                      labelFormatter={(d) => `งวด ${roundDateToDate(d) || d}`}
                    />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11, paddingBottom: 6 }}
                      formatter={(v) =>
                        v === "soldPrizeAmount"
                          ? "เงินรางวัลลูกค้า (฿)"
                          : "รางวัลในสลากค้างสต็อก (฿)"
                      }
                    />
                    <Bar
                      dataKey="soldPrizeAmount"
                      name="soldPrizeAmount"
                      fill={COLORS.emerald}
                      radius={[3, 3, 0, 0]}
                      maxBarSize={28}
                    />
                    <Bar
                      dataKey="unsoldPrizeAmount"
                      name="unsoldPrizeAmount"
                      fill="#f59e0b"
                      radius={[3, 3, 0, 0]}
                      maxBarSize={28}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Lottery Draw Statistics */}
              <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                    <FiAward className="text-amber-500" /> เลขท้าย 2
                    ตัวที่ออกบ่อยที่สุดในปี {selectedYear}
                  </h3>
                  <span className="text-[11px] text-slate-500">
                    บันทึก {lotteryDrawStats.totalDrawsRecorded || 0} งวด
                  </span>
                </div>

                <div className="grid grid-cols-5 gap-2.5 mb-4">
                  {(lotteryDrawStats.topDrawnNumbers || [])
                    .slice(0, 5)
                    .map((num, idx) => (
                      <div
                        key={idx}
                        className="p-2.5 rounded-md bg-white dark:bg-slate-700/60 border border-slate-200/60 dark:border-slate-600 text-center shadow-xs"
                      >
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-950 px-1 rounded">
                          #{idx + 1}
                        </span>
                        <div className="text-xl font-bold font-mono text-slate-800 dark:text-white mt-1">
                          {num.number}
                        </div>
                        <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                          ออก {num.count} ครั้ง
                        </div>
                      </div>
                    ))}
                </div>

                <p className="text-[11px] text-slate-500">
                  สถิติเลขท้าย 2
                  ตัวข้างต้นเป็นเลขยอดนิยมที่ออกซ้ำมากที่สุดในรอบปี
                </p>
              </div>

              {/* CSR & Community Impact */}
              <div className="p-4 rounded-md border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-col justify-between">
                <div>
                  <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-1.5">
                    <FiHeart className="text-rose-500" />{" "}
                    กิจกรรมเพื่อสังคมและการมีส่วนร่วม
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-md bg-white dark:bg-slate-700/60 border border-slate-200/60 dark:border-slate-600">
                      <div className="flex items-center gap-2 text-rose-600 text-xs font-bold">
                        <FiHeart /> คำสั่งซื้อร่วมบริจาค
                      </div>
                      <div className="text-2xl font-bold font-mono text-slate-800 dark:text-white mt-2">
                        {Number(
                          communityStats.donateOrdersCount || 0,
                        ).toLocaleString()}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        รายการบริจาคผ่านระบบ
                      </p>
                    </div>

                    <div className="p-4 rounded-md bg-white dark:bg-slate-700/60 border border-slate-200/60 dark:border-slate-600">
                      <div className="flex items-center gap-2 text-purple-600 text-xs font-bold">
                        <FiTarget /> ภารกิจสำเร็จ
                      </div>
                      <div className="text-2xl font-bold font-mono text-slate-800 dark:text-white mt-2">
                        {Number(
                          communityStats.missionOrdersCount || 0,
                        ).toLocaleString()}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        ภารกิจที่สมาชิกร่วมทำ
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-[11px] text-slate-500 border-t border-slate-200/60 pt-2">
                  ระบบวันดีร่วมส่งเสริมการคืนประโยชน์สู่สังคมควบคู่กับการเติบโตของธุรกิจ
                </div>
              </div>
            </div>
          </motion.section>

          {/* =========================================================================
              PANEL 5: MASTER EXPANDABLE TABLE & HALL OF FAME
              ========================================================================= */}
          <motion.section
            id="panel-mastertable"
            variants={panelVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="bg-white dark:bg-[#22272e] p-6 rounded-md shadow-sm border border-slate-200 dark:border-slate-800 scroll-mt-24"
          >
            {/* Panel Header */}
            <div className="flex justify-between items-center pb-4 mb-5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 flex items-center justify-center font-bold text-sm">
                  5
                </span>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                    ตารางข้อมูลดิบรายงวดและลูกค้าประจำแห่งปี
                  </h2>
                  <p className="text-xs text-slate-500">
                    คลิกที่แต่ละแถวเพื่อขยายดูรายละเอียดเชิงลึกของแต่ละงวด
                  </p>
                </div>
              </div>
            </div>

            {/* Hall of Fame Summary */}
            <div className="mb-6 p-4 rounded-md border border-amber-200/60 bg-amber-50/40 dark:bg-amber-950/20 dark:border-amber-900/50">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-amber-900 dark:text-amber-300 flex items-center gap-1.5">
                  <FiStar className="text-amber-500" /> ลูกค้าประจำแห่งปี
                </h3>
                <span className="text-[11px] text-amber-800 dark:text-amber-400 font-semibold">
                  ความสม่ำเสมอสูงสุดตลอดปี {selectedYear}
                </span>
              </div>
              <p className="text-xs text-amber-900 dark:text-amber-200 mb-2">
                นับงวดที่ซื้อสำเร็จ ไม่ซ้ำงวด ·
                จำนวนงวดเท่ากันเรียงตามยอดซื้อรวม แล้วรหัสสมาชิก
              </p>
              <div className="flex flex-wrap gap-2 items-center mb-2 text-xs">
                <select
                  aria-label="งวดที่ต้องการคำนวณ"
                  className="border rounded p-2 dark:bg-slate-800"
                  disabled={!!loyaltyProgress}
                  value={
                    loyaltyRoundDates.includes(loyaltyRound)
                      ? loyaltyRound
                      : loyaltyRoundDates[loyaltyRoundDates.length - 1] || ""
                  }
                  onChange={(e) => setLoyaltyRound(e.target.value)}
                >
                  {loyaltyRoundDates.map((rd) => (
                    <option key={rd} value={rd}>
                      {rd} —{" "}
                      {data?.customerLoyalty?.rounds?.[rd]
                        ? `อัปเดต ${dayjs(data.customerLoyalty.rounds[rd].updatedAt).format("DD/MM/YYYY HH:mm")}`
                        : "ยังไม่คำนวณ"}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="border rounded p-2 disabled:opacity-50"
                  disabled={!!loyaltyProgress || !loyaltyRoundDates.length}
                  onClick={() => recalculateLoyalty(false)}
                >
                  อัปเดตงวดที่เลือก
                </button>
                <button
                  type="button"
                  className="border rounded p-2 disabled:opacity-50"
                  disabled={!!loyaltyProgress}
                  onClick={() => recalculateLoyalty(true)}
                >
                  คำนวณใหม่ทั้งปี
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-2">
                คำนวณทั้งปีจะอัปเดตทีละงวด กรุณาเปิดหน้านี้ไว้จนเสร็จ · รวม{" "}
                {loyaltyRoundDates.length} งวดในรายงาน
              </p>
              {missingLoyaltyRounds.length > 0 && (
                <p className="text-xs text-amber-700 mb-2">
                  ข้อมูลยังไม่ครบ: ยังไม่คำนวณ {missingLoyaltyRounds.join(", ")}
                </p>
              )}
              <p className="text-xs text-slate-500 mb-3" role="status">
                {loyaltyProgress ||
                  loyaltyMessage ||
                  (data?.customerLoyalty
                    ? `คำนวณล่าสุด ${dayjs(data.customerLoyalty.updatedAt).format("DD/MM/YYYY HH:mm")} · ยังไม่คำนวณ ${missingLoyaltyRounds.length} งวด`
                    : "ยังไม่เคยคำนวณ กรุณากดคำนวณใหม่ทั้งปี")}
              </p>
              {data?.customerLoyalty && !topLoyalCustomers.length && (
                <p className="text-xs mb-2">
                  ไม่พบสมาชิกที่มีคำสั่งซื้อสำเร็จในงวดที่คำนวณแล้ว
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                {topLoyalCustomers.slice(0, 5).map((user, idx) => (
                  <div
                    key={idx}
                    className="p-2.5 rounded bg-white dark:bg-slate-800 border border-amber-200/50 dark:border-slate-700 text-xs"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-bold text-amber-600">
                        #{idx + 1}
                      </span>
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950 px-1 rounded">
                        {user.roundsAppeared} งวด
                      </span>
                    </div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200 truncate mt-1">
                      {user.name || `User #${user.userId}`}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                      ID: {user.userId}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {Number(user.tickets || 0).toLocaleString()} ใบ ·{" "}
                      {Number(user.amount || 0).toLocaleString()} บาท
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Unified Expandable Master Table */}
            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-md">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300">
                    <th className="py-2.5 px-3 font-bold w-10 text-center"></th>
                    <th className="py-2.5 px-3 font-bold">งวดวันที่</th>
                    <th className="py-2.5 px-3 font-bold text-right">ยอดขาย</th>
                    <th className="py-2.5 px-3 font-bold text-right">
                      สลากจำหน่ายได้
                    </th>
                    <th className="py-2.5 px-3 font-bold text-right">
                      สลากเหลือ
                    </th>
                    <th className="py-2.5 px-3 font-bold text-right">
                      โควตารวม
                    </th>
                    <th className="py-2.5 px-3 font-bold text-center">
                      อัตราขายหมด
                    </th>
                    <th className="py-2.5 px-3 font-bold text-right">
                      ถูกรางวัล
                    </th>
                    <th className="py-2.5 px-3 font-bold text-right">
                      รางวัลที่ไม่ได้ขาย
                    </th>
                    <th className="py-2.5 px-3 font-bold text-center">
                      รางวัลที่ 1
                    </th>
                    <th className="py-2.5 px-3 font-bold text-center">
                      เลขท้าย
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                  {rounds.map((r, i) => {
                    const isExpanded = expandedRound === r.roundDate;
                    return (
                      <React.Fragment key={i}>
                        <tr
                          onClick={() =>
                            setExpandedRound(isExpanded ? null : r.roundDate)
                          }
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                        >
                          <td className="py-2.5 px-3 text-center text-slate-400">
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </td>
                          <td className="py-2.5 px-3 font-sans font-semibold text-slate-800 dark:text-slate-200">
                            {roundDateToDate(r.roundDate) || r.roundDate}
                            {r.isBestIncome && (
                              <span className="ml-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-950 px-1 rounded">
                                ยอดขายสูงสุด
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-slate-900 dark:text-white">
                            {Number(r.income).toLocaleString()} ฿
                          </td>
                          <td className="py-2.5 px-3 text-right text-emerald-600 font-bold">
                            {Number(r.ticketsSold).toLocaleString()} ใบ
                          </td>
                          <td className="py-2.5 px-3 text-right text-slate-500">
                            {Number(
                              r.remainingTickets ??
                                Math.max(0, r.allocated - r.ticketsSold),
                            ).toLocaleString()}{" "}
                            ใบ
                          </td>
                          <td className="py-2.5 px-3 text-right text-slate-400">
                            {Number(r.allocated).toLocaleString()} ใบ
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-bold ${
                                r.sellThroughRate >= 95
                                  ? "bg-emerald-50 text-emerald-700"
                                  : r.sellThroughRate >= 80
                                    ? "bg-blue-50 text-blue-700"
                                    : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              {r.sellThroughRate} %
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <span className="font-bold text-emerald-700 dark:text-emerald-400">
                              {Number(
                                r.soldWinningTickets || 0,
                              ).toLocaleString()}{" "}
                              ใบ
                            </span>
                            <span className="text-[10px] text-slate-400 block font-normal">
                              ({Number(r.soldPrizeAmount || 0).toLocaleString()}{" "}
                              ฿)
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {r.unsoldWinningTickets > 0 ? (
                              <>
                                <span className="font-bold text-amber-600 dark:text-amber-400">
                                  {Number(
                                    r.unsoldWinningTickets,
                                  ).toLocaleString()}{" "}
                                  ใบ
                                </span>
                                <span className="text-[10px] text-slate-400 block font-normal">
                                  (
                                  {Number(
                                    r.unsoldPrizeAmount || 0,
                                  ).toLocaleString()}{" "}
                                  ฿)
                                </span>
                              </>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center font-bold text-blue-600">
                            {r.firstPrize || "-"}
                          </td>
                          <td className="py-2.5 px-3 text-center font-bold text-amber-600">
                            {r.lastTwo || "-"}
                          </td>
                        </tr>

                        {/* Expanded Row Sub-Metrics */}
                        {isExpanded && (
                          <tr className="bg-slate-50/70 dark:bg-slate-800/40">
                            <td colSpan="11" className="py-4 px-6 font-sans">
                              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-xs pb-3 border-b border-slate-200/60 dark:border-slate-700">
                                <div>
                                  <span className="text-slate-400 block text-[10px]">
                                    ผู้ถูกรางวัล:
                                  </span>
                                  <strong className="text-emerald-700 font-mono">
                                    {Number(
                                      r.winnerCount || 0,
                                    ).toLocaleString()}{" "}
                                    คน
                                  </strong>
                                  <span className="text-slate-400 text-[10px] block">
                                    (
                                    {Number(
                                      r.soldWinningTickets || 0,
                                    ).toLocaleString()}{" "}
                                    ใบ)
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px]">
                                    ยอดขาย LINE ช้อปปิ้ง:
                                  </span>
                                  <strong className="text-emerald-700 font-mono">
                                    {r.lineIncome > 0
                                      ? `${Number(r.lineIncome).toLocaleString()} ฿`
                                      : "-"}
                                  </strong>
                                  <span className="text-slate-400 text-[10px] block">
                                    ({r.lineOrders || 0} ออเดอร์)
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px]">
                                    ยอดจำหน่ายตลาดลาว:
                                  </span>
                                  <strong className="text-purple-700 font-mono">
                                    {r.laoIncomeLak > 0
                                      ? `${Number(r.laoIncomeLak).toLocaleString()} กีบ`
                                      : "-"}
                                  </strong>
                                  <span className="text-slate-400 text-[10px] block">
                                    ({r.laoOrders || 0} ออเดอร์)
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px]">
                                    เฉลี่ยต่อคำสั่งซื้อ:
                                  </span>
                                  <strong className="text-slate-700 font-mono">
                                    {Number(
                                      r.avgSpending || 0,
                                    ).toLocaleString()}{" "}
                                    ฿
                                  </strong>{" "}
                                  / {r.avgTickets || 0} ใบ
                                </div>
                                <div>
                                  <span className="text-slate-400 block text-[10px]">
                                    ออเดอร์ที่ถูกยกเลิก:
                                  </span>
                                  <strong className="text-rose-600 font-mono">
                                    {Number(
                                      r.canceledOrders || 0,
                                    ).toLocaleString()}
                                  </strong>{" "}
                                  รายการ
                                </div>
                              </div>

                              {/* Prize breakdown badges */}
                              <div className="pt-3 grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
                                <div>
                                  <span className="text-slate-500 font-bold block mb-1.5">
                                    รางวัลที่ลูกค้าได้รับในงวดนี้:
                                  </span>
                                  {r.soldBreakdown &&
                                  Object.keys(r.soldBreakdown).length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {Object.entries(r.soldBreakdown).map(
                                        ([pKey, pCount]) => (
                                          <span
                                            key={pKey}
                                            className="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200/60"
                                          >
                                            {PRIZE_NAMES[pKey] || pKey}:{" "}
                                            <strong>{pCount} ใบ</strong>
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-[11px]">
                                      ไม่มีข้อมูลรางวัล
                                    </span>
                                  )}
                                </div>

                                <div>
                                  <span className="text-slate-500 font-bold block mb-1.5">
                                    รางวัลตกค้างในสลากที่ไม่ได้ขาย:
                                  </span>
                                  {r.unsoldBreakdown &&
                                  Object.keys(r.unsoldBreakdown).length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {Object.entries(r.unsoldBreakdown).map(
                                        ([pKey, pCount]) => (
                                          <span
                                            key={pKey}
                                            className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-200/60"
                                          >
                                            {PRIZE_NAMES[pKey] || pKey}:{" "}
                                            <strong>{pCount} ใบ</strong>{" "}
                                            (หลุดรางวัล)
                                          </span>
                                        ),
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-[11px]">
                                      ไม่มีรางวัลตกค้างในสลากคงเหลือ
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.section>
        </div>
      )}
    </div>
  );
};

export default AnnualReport;
